/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import React, { useEffect, useState } from 'react';
import { useKPIContext } from '@/providers/KpiProvider';
import { useLanguage } from '@/providers/LanguageProvider';
import { MAX_FACT_IDS } from '@/utils/constants';
import Icons from '@/ux-common/src/icons/icons';
import { Component } from 'types/api-data';

import { multiSelectDataTransform } from '@/lib/helper';
import { useAppDetails } from '@/hooks/useAppDetails';
import { EnhancedMultiSelect } from '@/components/combobox';
import { EnhancedSelect } from '@/components/combobox/enhanced-select';
import { CustomButton } from '@/components/common/custom-button';
import { Icons as PortalIcons, System } from '@/components/icons';

import { FACT_NAME_LABEL_MAP } from '../fact-name-label-map';
import { formatDateToTodayTime, truncateTraceId } from '../traces/traceUtils';

interface FilterPanelProps {
  onApply: (
    workflows: string | null,
    factFilter: string | null,
    factIds: string | null
  ) => void;
  currentWorkflows: string | null;
  currentFactFilter: string | null;
  currentFactIds: string | null;
  activeRange?: {
    from: Date | null;
    to: Date | null;
  };
  factsData?: { key: string; label: string }[];
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  onApply,
  currentWorkflows,
  currentFactFilter,
  currentFactIds,
  activeRange,
  factsData,
}) => {
  const { messages } = useLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedWorkflows, setSelectedWorkflows] = useState<string>(
    currentWorkflows || ''
  );
  const [selectedFactFilter, setSelectedFactFilter] = useState<string | null>(
    currentFactFilter
  );
  const [selectedFactIds, setSelectedFactIds] = useState<string>(
    currentFactIds || ''
  );
  const [factIdsError, setFactIdsError] = useState(false);
  const [filterFactIdOptions, setFilterFactIdOptions] = useState<
    { id: number; label: string; value: string; time: string }[]
  >([]);
  const [filterFactIdsLoading, setFilterFactIdsLoading] = useState(false);

  const { data, loading } = useAppDetails();
  const { fetchInsightsFactIds } = useKPIContext();

  const workflows =
    data?.components?.filter((component: Component) =>
      component.type.includes('workflow')
    ) || [];

  const transformedWorkflows = multiSelectDataTransform(workflows) ?? [];

  useEffect(() => {
    const loadFilterFactIds = async () => {
      if (!data?.name || !selectedFactFilter || !fetchInsightsFactIds) {
        setFilterFactIdOptions([]);
        return;
      }

      try {
        setFilterFactIdsLoading(true);

        const factIdsMap = await fetchInsightsFactIds(
          data.name,
          selectedFactFilter,
          selectedWorkflows,
          activeRange
        );

        const options = Object.keys(factIdsMap).map((factId, index) => {
          const endTime = factIdsMap?.[factId]?.end_time;
          const formattedEndTime = formatDateToTodayTime(endTime);

          return {
            id: index,
            label: truncateTraceId(factId),
            value: factId,
            time: formattedEndTime || '',
          };
        });

        setFilterFactIdOptions(options);
      } catch (error) {
        console.error('Error loading filter fact IDs:', error);
        setFilterFactIdOptions([]);
      } finally {
        setFilterFactIdsLoading(false);
      }
    };

    loadFilterFactIds();
  }, [
    activeRange,
    data?.name,
    fetchInsightsFactIds,
    selectedFactFilter,
    selectedWorkflows,
  ]);

  useEffect(() => {
    if (!selectedFactFilter) return;
    if (!selectedFactIds) return;
    if (filterFactIdsLoading) return;

    const selectedValues = selectedFactIds.split(',').filter(Boolean);
    const optionValues = new Set(
      filterFactIdOptions.map((option) => option.value)
    );
    const validSelectedValues = selectedValues.filter((value: string) =>
      optionValues.has(value)
    );

    if (validSelectedValues.length === selectedValues.length) return;

    setSelectedFactIds(validSelectedValues.join(','));
    setFactIdsError(false);
  }, [
    filterFactIdOptions,
    filterFactIdsLoading,
    selectedFactFilter,
    selectedFactIds,
  ]);

  const handleApply = () => {
    if (selectedFactFilter && !selectedFactIds) {
      setFactIdsError(true);
      return;
    }
    onApply(
      selectedWorkflows || null,
      selectedFactFilter,
      selectedFactIds || null
    );
    setIsOpen(false);
  };

  const handleClearAll = () => {
    setSelectedWorkflows('');
    setSelectedFactFilter(null);
    setSelectedFactIds('');
    onApply(null, null, null);
    setIsOpen(false);
  };

  const handleOpen = () => {
    setSelectedWorkflows(currentWorkflows || '');
    setSelectedFactFilter(currentFactFilter);
    setSelectedFactIds(currentFactIds || '');
    setFactIdsError(false);
    setIsOpen(true);
  };

  const workflowsChanged = selectedWorkflows !== (currentWorkflows || '');
  const factFilterChanged = selectedFactFilter !== (currentFactFilter || null);
  const factIdsChanged = selectedFactIds !== (currentFactIds || '');
  const hasChanges = workflowsChanged || factFilterChanged || factIdsChanged;

  return (
    <div className="relative">
      <button
        onClick={handleOpen}
        className="relative flex items-center justify-center rounded hover:bg-card-01"
      >
        {PortalIcons.FilterIcon('text-white')}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          <div className="absolute right-0 top-full z-50 mt-1 w-[480px] rounded-lg border border-form-control-border-disabled bg-card-00">
            <div className="flex items-center justify-between border-b border-line-secondary p-4">
              <div className="flex items-center">
                {PortalIcons.FilterIcon('text-[#34BCAF] size-8')}
                <span className="text-2xl text-body-primary">
                  {messages?.General.filters}{' '}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <CustomButton
                  customVariant="ghost-secondary"
                  customSize="small"
                  onClick={() => {
                    handleClearAll();
                  }}
                  disabled={
                    !selectedWorkflows &&
                    !selectedFactFilter &&
                    !selectedFactIds
                  }
                >
                  {messages?.General.clear_all_filters}{' '}
                </CustomButton>
                <button
                  onClick={() => setIsOpen(false)}
                  className="rounded p-1 text-body-subtle hover:bg-[#257C9D1F]  focus:bg-[#257C9D1F] focus:text-[#257C9D1F]"
                >
                  {Icons.Close('size-5 text-[#C1C9CF] hover:text-[#EBEFF1]')}
                </button>
              </div>
            </div>

            <div className="px-7 py-4">
              <label
                htmlFor="filter-workflow"
                className="mb-2 block text-kpi-label text-form-input-rest"
              >
                {messages?.PromptFilters.multi_select.workflow}
              </label>
              <EnhancedMultiSelect
                id="filter-workflow"
                options={transformedWorkflows}
                defaultValue={selectedWorkflows.split(',').filter(Boolean)}
                onValueChange={(values) => {
                  setSelectedWorkflows(values.join(','));
                  setSelectedFactIds('');
                  setFactIdsError(false);
                }}
                placeholder={
                  messages?.PromptFilters.multi_select.select_workflow
                }
                variant="inverted"
                maxCount={2}
                loading={loading}
                maxOptions={5}
              />
            </div>

            <div className=" px-7 py-4">
              <label
                htmlFor="filter-fact-filter"
                className="mb-2 block text-kpi-label text-form-input-rest"
              >
                {messages?.PromptFilters.multi_select.fact}
              </label>
              <div className="flex h-10 items-center rounded-e rounded-s border-2 border-form-control-border-rest px-3 py-1">
                <EnhancedSelect
                  id="filter-fact-filter"
                  value={selectedFactFilter || undefined}
                  onValueChange={(newFactFilter) => {
                    setSelectedFactFilter(newFactFilter);
                    setSelectedFactIds('');
                    setFactIdsError(false);
                  }}
                  onClear={() => {
                    setSelectedFactFilter(null);
                    setSelectedFactIds('');
                    setFactIdsError(false);
                  }}
                  placeholder={
                    messages?.PromptFilters.multi_select
                      .select_fact_to_enable_id_selection
                  }
                  options={
                    factsData?.map((fact) => ({
                      value: fact.key,
                      label:
                        FACT_NAME_LABEL_MAP?.[fact.key]?.label ||
                        fact.label ||
                        fact.key,
                    })) ?? []
                  }
                />
              </div>
              <div className="mt-3">
                <label className="sr-only" htmlFor="fact-filter-ids">
                  {messages?.PromptFilters.multi_select.select_fact_ids}
                </label>
                <EnhancedMultiSelect
                  key={`insights-fact-ids-${selectedFactFilter}-${selectedWorkflows}-${selectedFactIds}`}
                  id="fact-filter-ids"
                  options={filterFactIdOptions}
                  defaultValue={selectedFactIds.split(',').filter(Boolean)}
                  onValueChange={(values) => {
                    setSelectedFactIds(values.length ? values.join(',') : '');
                    if (values.length > 0) setFactIdsError(false);
                  }}
                  placeholder={
                    messages?.PromptFilters.multi_select.select_fact_ids
                  }
                  inputPlaceholder={
                    messages?.PromptFilters.multi_select.search_by_fact_id
                  }
                  variant="inverted"
                  maxCount={1}
                  loading={filterFactIdsLoading}
                  maxOptions={MAX_FACT_IDS}
                  disabled={!selectedFactFilter}
                />
                <p
                  className={`mt-2 flex items-center gap-1 text-sm ${factIdsError ? 'text-status-error text-red-500' : 'text-form-input-rest'}`}
                >
                  {System.InfoIcon('size-4')}
                  {factIdsError
                    ? messages?.PromptFilters.multi_select
                        .please_enter_atleast_one_fact_id
                    : messages?.PromptFilters.multi_select
                        .atleast_one_fact_id_required}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-line-secondary px-7 py-4">
              <div className="flex gap-2">
                <CustomButton
                  customSize="regular"
                  customVariant="primary"
                  onClick={handleApply}
                  disabled={!hasChanges}
                >
                  {messages?.PromptFilters.date_range.apply}
                </CustomButton>
                <CustomButton
                  customSize="regular"
                  customVariant="secondary"
                  onClick={() => setIsOpen(false)}
                >
                  {messages?.General.cancel}{' '}
                </CustomButton>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
